import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { FinanceAnalytics } from '../components/FinanceAnalytics';
import { FinancialAidPanel } from '../components/FinancialAidPanel';
import { RazorpayPayButton } from '../components/RazorpayPayButton';
import { money } from '../lib/money';
import { toast } from 'sonner';

export default function Fees() {
  const { user } = useAuth();
  const { t } = useI18n();
  const qc = useQueryClient();
  const isFin = ['finance_admin', 'school_admin', 'principal', 'global_admin'].includes(user?.role);
  const isGuardian = user?.role === 'guardian';
  const { data: invoices } = useQuery({ queryKey: ['invoices'], queryFn: () => api.get('/invoices').then((r) => r.data) });
  const { data: instReqs } = useQuery({ queryKey: ['installment-requests'], queryFn: () => api.get('/finance/installment-requests').then((r) => r.data), enabled: isFin || isGuardian });
  const { data: arrears } = useQuery({ queryKey: ['arrears'], queryFn: () => api.get('/finance/arrears').then((r) => r.data), enabled: isFin });
  const { data: plans } = useQuery({ queryKey: ['fee-plans'], queryFn: () => api.get('/fee-plans').then((r) => r.data), enabled: isFin });
  const { data: tenants } = useQuery({ queryKey: ['tenants'], queryFn: () => api.get('/tenants').then((r) => r.data), enabled: isFin });
  const school = (tenants || []).find((x) => x.type === 'school' && x.country === 'IN') || (tenants || []).find((x) => x.type === 'school');
  const { data: receipts } = useQuery({ queryKey: ['receipts'], queryFn: () => api.get('/receipts').then((r) => r.data), enabled: isFin || ['guardian', 'student'].includes(user?.role) });
  const { data: rzpStatus } = useQuery({ queryKey: ['indiapay-status'], queryFn: () => api.get('/indiapay/status').then((r) => r.data) });
  const [planOpen, setPlanOpen] = useState(false);
  const [view, setView] = useState('billing');
  const [plan, setPlan] = useState({ name: '', grade: '5', amount: '', installments: '4', category: 'general' });

  const planMut = useMutation({
    mutationFn: () => api.post('/fee-plans', { tenant_id: school?.id, name: plan.name, grade: plan.grade, amount: parseFloat(plan.amount), installments: parseInt(plan.installments, 10) || 1, category: plan.category }),
    onSuccess: () => { toast.success('Fee plan created'); setPlanOpen(false); setPlan({ ...plan, name: '', amount: '' }); qc.invalidateQueries({ queryKey: ['fee-plans'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const genMut = useMutation({
    mutationFn: (planId) => api.post('/invoices/generate', { fee_plan_id: planId }),
    onSuccess: (r) => { toast.success(`${r.data.created} invoice(s) generated`); qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['arrears'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const payMut = useMutation({
    mutationFn: (inv) => api.post('/payments/checkout', { invoice_id: inv.id, origin_url: window.location.origin }),
    onSuccess: (r) => { window.location.href = r.data.checkout_url; },
    onError: (e) => toast.error(errMsg(e)),
  });
  const recordMut = useMutation({
    mutationFn: (inv) => api.post(`/invoices/${inv.id}/payments`, { amount: inv.installment_amount, method: 'cash' }),
    onSuccess: () => { toast.success('Installment payment recorded'); qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['arrears'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const remindMut = useMutation({
    mutationFn: (inv) => api.post(`/invoices/${inv.id}/remind`),
    onSuccess: (r) => toast.success(`Reminder sent to ${r.data.recipients} guardian(s) - email: ${r.data.email_status}`),
    onError: (e) => toast.error(errMsg(e)),
  });
  const remindAllMut = useMutation({
    mutationFn: () => api.post('/finance/remind-overdue'),
    onSuccess: (r) => toast.success(`${r.data.invoices_reminded} overdue invoice(s) reminded - ${r.data.recipients} guardian message(s)`),
    onError: (e) => toast.error(errMsg(e)),
  });
  const emailStatementsMut = useMutation({
    mutationFn: () => api.post('/finance/statements/email-all'),
    onSuccess: (r) => toast.success(`${r.data.statements_sent} statement(s) emailed to guardians (${r.data.email_status})`),
    onError: (e) => toast.error(errMsg(e)),
  });
  const creditMut = useMutation({
    mutationFn: ({ id, amount, reason }) => api.post(`/invoices/${id}/credit-note`, { amount, reason }),
    onSuccess: (r) => { toast.success(`Credit note ${r.data.note_no} issued`); qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['arrears'] }); qc.invalidateQueries({ queryKey: ['fin-analytics'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const writeOffMut = useMutation({
    mutationFn: ({ id, reason }) => api.post(`/invoices/${id}/write-off`, { reason }),
    onSuccess: () => { toast.success('Invoice written off'); qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['arrears'] }); qc.invalidateQueries({ queryKey: ['fin-analytics'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const doCredit = (inv) => {
    const amount = parseFloat(window.prompt(`Credit note amount (outstanding ₹${inv.balance.toLocaleString()}):`));
    if (!amount) return;
    const reason = window.prompt('Reason for credit note:');
    if (!reason) return;
    creditMut.mutate({ id: inv.id, amount, reason });
  };
  const doWriteOff = (inv) => {
    const reason = window.prompt(`Write off ₹${inv.balance.toLocaleString()} for ${inv.student_name}? Enter reason:`);
    if (!reason) return;
    writeOffMut.mutate({ id: inv.id, reason });
  };
  const exportCsv = async () => {
    try {
      const r = await api.get('/finance/invoices/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'invoices.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const downloadStatement = async (sid, name) => {
    try {
      const r = await api.get(`/finance/statements/${sid}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `fee-statement-${name.replace(/ /g, '-')}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch (e) { toast.error(errMsg(e)); }
  };
  const statementStudents = Object.values((invoices || []).reduce((acc, i) => { acc[i.student_id] = { id: i.student_id, name: i.student_name }; return acc; }, {}));
  const instReqMut = useMutation({
    mutationFn: ({ invoice_id, installments, note }) => api.post('/finance/installment-requests', { invoice_id, installments, note }),
    onSuccess: () => { toast.success('Installment plan requested - the finance office will review it'); qc.invalidateQueries({ queryKey: ['installment-requests'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const decideMut = useMutation({
    mutationFn: ({ id, approve, note }) => api.post(`/finance/installment-requests/${id}/decide`, { approve, note }),
    onSuccess: (r) => { toast.success(`Request ${r.data.status}`); qc.invalidateQueries({ queryKey: ['installment-requests'] }); qc.invalidateQueries({ queryKey: ['invoices'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const requestSplit = (inv) => {
    const n = parseInt(window.prompt(`Split ₹${inv.balance.toLocaleString()} into how many installments? (2-6)`, '3'), 10);
    if (!n) return;
    const note = window.prompt('Add a note for the finance office (optional):') || '';
    instReqMut.mutate({ invoice_id: inv.id, installments: n, note });
  };
  const pendingByInvoice = (instReqs || []).reduce((acc, r) => { if (r.status === 'pending') acc[r.invoice_id] = r; return acc; }, {});
  const pendingReqs = (instReqs || []).filter((r) => r.status === 'pending');
  const { data: familyStmt } = useQuery({ queryKey: ['family-statement'], queryFn: () => api.get('/finance/family-statement').then((r) => r.data), enabled: isGuardian, retry: false });
  const { data: upi } = useQuery({ queryKey: ['upi-info'], queryFn: () => api.get('/india/upi-info').then((r) => r.data), enabled: isGuardian || user?.role === 'student', retry: false });
  const upiLink = (i) => `upi://pay?pa=${upi.vpa}&pn=${encodeURIComponent(upi.payee)}&am=${i.balance}&cu=INR&tn=${encodeURIComponent(`${i.plan_name} ${i.student_name}`.slice(0, 60))}`;
  const [invStatus, setInvStatus] = useState('all');
  const [invQ, setInvQ] = useState('');
  const shownInvoices = (invoices || []).filter((i) =>
    (invStatus === 'all' || i.status === invStatus) &&
    (!invQ || `${i.student_name} ${i.plan_name}`.toLowerCase().includes(invQ.toLowerCase())));
  const autopayMut = useMutation({
    mutationFn: ({ id, enabled }) => api.post(`/invoices/${id}/autopay`, { enabled }),
    onSuccess: (r) => { toast.success(r.data.enabled ? 'Autopay on - you get a one-click pay prompt on every due date' : 'Autopay off'); qc.invalidateQueries({ queryKey: ['invoices'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const familyPayMut = useMutation({
    mutationFn: () => api.post('/finance/family-checkout', { invoice_ids: familyStmt.payable_invoice_ids, origin_url: window.location.origin }),
    onSuccess: (r) => { window.location.href = r.data.checkout_url; },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid="fees-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t('Fees & Finance')}</h1>
        {isFin && (
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="export-invoices-button" variant="outline" onClick={exportCsv}>Export CSV</Button>
            <Button data-testid="remind-all-button" variant="outline" disabled={remindAllMut.isPending} onClick={() => remindAllMut.mutate()}>Remind all overdue</Button>
            <Button data-testid="email-statements-button" variant="outline" disabled={emailStatementsMut.isPending} onClick={() => emailStatementsMut.mutate()}>Email statements now</Button>
            <Dialog open={planOpen} onOpenChange={setPlanOpen}>
            <DialogTrigger asChild><Button data-testid="new-fee-plan-button" className="bg-slate-900 hover:bg-slate-800">{t('New fee plan')}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">{t('New fee plan')}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input data-testid="plan-name-input" placeholder="Plan name (e.g. Grade 6 Annual Fee 2026-27)" value={plan.name} onChange={(e) => setPlan({ ...plan, name: e.target.value })} />
                <div className="grid grid-cols-3 gap-2">
                  <Input data-testid="plan-grade-input" placeholder={t('Grade')} value={plan.grade} onChange={(e) => setPlan({ ...plan, grade: e.target.value })} />
                  <Input data-testid="plan-amount-input" type="number" placeholder={t('Amount (₹)')} value={plan.amount} onChange={(e) => setPlan({ ...plan, amount: e.target.value })} />
                  <Input data-testid="plan-installments-input" type="number" placeholder={t('Installments')} value={plan.installments} onChange={(e) => setPlan({ ...plan, installments: e.target.value })} />
                </div>
                <select data-testid="plan-category-select" value={plan.category} onChange={(e) => setPlan({ ...plan, category: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                  {['general', 'scholarship', 'staff_ward', 'sibling'].map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                </select>
                <Button data-testid="plan-create-button" disabled={!plan.name || !plan.amount || planMut.isPending} onClick={() => planMut.mutate()} className="w-full bg-slate-900 hover:bg-slate-800">{t('Create plan')}</Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>
      <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-6">{t('Hard rule: arrears never block academic or safeguarding records - optional services only')}</p>

      {isFin && (
        <div className="flex gap-1 mb-5 border-b border-slate-200" data-testid="fees-view-tabs">
          {[['billing', 'Fees & Billing'], ['aid', 'Financial Aid']].map(([k, label]) => (
            <button key={k} data-testid={`fees-tab-${k}`} onClick={() => setView(k)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${view === k ? 'border-slate-900 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>{label}</button>
          ))}
        </div>
      )}

      {isFin && view === 'aid' && <FinancialAidPanel />}

      {view === 'billing' && (<>
      {isGuardian && familyStmt && familyStmt.children.length > 0 && (
        <div className="bg-white border border-slate-200 border-t-4 border-t-orange-500 rounded-md p-5 mb-6 stagger-in" data-testid="family-billing-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-display text-lg font-semibold">Family statement</h2>
              <p className="font-mono-label text-[10px] uppercase text-slate-400">All your children's fees, combined into one bill</p>
            </div>
            {familyStmt.family.outstanding > 0 && (
              <Button data-testid="family-pay-button" className="bg-orange-600 hover:bg-orange-500" disabled={familyPayMut.isPending}
                onClick={() => familyPayMut.mutate()}>
                Pay {money(familyStmt.family.outstanding, familyStmt.currency)} together
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            {familyStmt.children.map((c) => (
              <div key={c.student_id} className="border border-slate-100 rounded-md p-3" data-testid={`family-child-${c.student_id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm">{c.name} <span className="text-xs text-slate-400 font-normal">Grade {c.grade}{c.section}</span></p>
                  <span className={`font-display font-bold ${c.outstanding > 0 ? 'text-rose-600' : 'text-green-700'}`} data-testid={`family-child-due-${c.student_id}`}>{c.outstanding > 0 ? money(c.outstanding, familyStmt.currency) : 'Settled'}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{c.invoices.length} invoice(s) · charged {money(c.charged, familyStmt.currency)} · paid {money(c.paid, familyStmt.currency)}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 mt-3 pt-3 flex items-center justify-between text-sm">
            <span className="font-mono-label text-[10px] uppercase text-slate-400">Family total outstanding</span>
            <span className="font-display text-xl font-extrabold" data-testid="family-total-outstanding">{money(familyStmt.family.outstanding, familyStmt.currency)}</span>
          </div>
          {familyStmt.family.outstanding > 0 && <p className="text-[10px] text-slate-400 mt-1">One Stripe payment settles {familyStmt.payable_invoice_ids.length} open invoice(s) across your children - every invoice gets its own receipt.</p>}
        </div>
      )}

      {isFin && (plans || []).length > 0 && (
        <div className="mb-6">
          <h2 className="font-display text-lg font-semibold mb-3">{t('Fee plans')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fee-plans-list">
            {plans.map((p) => (
              <div key={p.id} className="bg-white border border-slate-200 rounded-md p-4 stagger-in" data-testid={`fee-plan-${p.id}`}>
                <p className="font-semibold text-sm">{p.name}</p>
                <p className="text-xs text-slate-500">{t('Grade')} {p.grade} · {money(p.amount, p.currency)} · {p.installments} {t('Installments').toLowerCase()} · {p.category}</p>
                <button data-testid={`gen-invoices-${p.id}`} onClick={() => genMut.mutate(p.id)}
                  className="mt-3 text-xs bg-slate-900 text-white rounded px-3 py-1.5 hover:bg-slate-800 transition-colors">{t('Generate invoices')}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isFin && arrears && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-md p-5 stagger-in" data-testid="arrears-total-card">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">{t('Total arrears')}</p>
            <p className="font-display text-3xl font-extrabold text-rose-600">₹{arrears.total_due.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5 stagger-in">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">{t('Open invoices')}</p>
            <p className="font-display text-3xl font-extrabold">{arrears.count}</p>
          </div>
          {arrears.aging && (
            <div className="bg-white border border-slate-200 rounded-md p-5 stagger-in col-span-2 md:col-span-1" data-testid="aging-buckets-card">
              <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">{t('Dues aging (days)')}</p>
              <div className="space-y-1">
                {Object.entries(arrears.aging).map(([bucket, amt]) => (
                  <div key={bucket} className="flex items-center justify-between text-sm" data-testid={`aging-${bucket}`}>
                    <span className={`font-mono-label text-[10px] ${bucket === '90+' ? 'text-rose-600 font-bold' : bucket === '61-90' ? 'text-amber-600' : 'text-slate-500'}`}>{bucket}</span>
                    <span className={`font-semibold ${bucket === '90+' && amt > 0 ? 'text-rose-600' : ''}`}>₹{amt.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {isFin && <FinanceAnalytics />}

      {isFin && pendingReqs.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-md p-4 mb-4" data-testid="installment-requests-panel">
          <p className="font-mono-label text-[10px] uppercase text-amber-700 mb-2">Installment plan requests awaiting review</p>
          <div className="divide-y divide-slate-100">
            {pendingReqs.map((r) => (
              <div key={r.id} className="py-2 flex items-center gap-2 flex-wrap text-sm" data-testid={`inst-req-${r.id}`}>
                <span className="font-medium">{r.student_name}</span>
                <span className="text-slate-400 text-xs">{r.plan_name}</span>
                <span className="font-mono-label text-[10px] uppercase bg-amber-50 text-amber-700 rounded px-1.5 py-0.5">{money(r.balance, r.currency)} ÷ {r.installments}</span>
                <span className="text-xs text-slate-500">by {r.guardian_name}{r.note ? ` - “${r.note}”` : ''}</span>
                <span className="ms-auto flex gap-2">
                  <button data-testid={`inst-approve-${r.id}`} onClick={() => decideMut.mutate({ id: r.id, approve: true, note: '' })} className="text-xs bg-green-600 text-white rounded px-2.5 py-1 hover:bg-green-500">Approve</button>
                  <button data-testid={`inst-reject-${r.id}`} onClick={() => { const note = window.prompt('Reason for rejection (sent to guardian):') || ''; decideMut.mutate({ id: r.id, approve: false, note }); }} className="text-xs border border-rose-200 text-rose-600 rounded px-2.5 py-1 hover:bg-rose-50">Reject</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="invoices-table">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-100">
          <Input data-testid="invoice-search-input" placeholder="Filter by student or plan..." value={invQ} onChange={(e) => setInvQ(e.target.value)} className="max-w-xs h-8 text-xs" />
          {['all', 'unpaid', 'partial', 'paid', 'written_off'].map((s) => (
            <button key={s} data-testid={`invoice-filter-${s}`} onClick={() => setInvStatus(s)}
              className={`text-xs rounded-full px-2.5 py-1 border capitalize ${invStatus === s ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:border-slate-900'}`}>{s.replace('_', ' ')}</button>
          ))}
          <span className="ms-auto text-[10px] text-slate-400">{shownInvoices.length} of {(invoices || []).length}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left">
            {['Student', 'Plan', 'Total', 'Paid', 'Balance', 'Status', 'Actions'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{t(h)}</th>)}
          </tr></thead>
          <tbody>
            {shownInvoices.map((i, idx) => (
              <tr key={i.id} className={`border-t border-slate-100 ${idx % 2 ? 'bg-slate-50/50' : ''}`}>
                <td className="py-2 px-3 font-medium">{i.student_name}</td>
                <td className="py-2 px-3">{i.plan_name}</td>
                <td className="py-2 px-3">{money(i.total, i.currency)}</td>
                <td className="py-2 px-3 text-green-700">{money(i.paid, i.currency)}</td>
                <td className="py-2 px-3 font-bold">{money(i.balance, i.currency)}</td>
                <td className="py-2 px-3"><span className={`text-xs rounded px-2 py-0.5 ${i.status === 'paid' ? 'bg-green-100 text-green-800' : i.status === 'partial' ? 'bg-amber-100 text-amber-800' : i.status === 'written_off' ? 'bg-slate-100 text-slate-500' : 'bg-rose-100 text-rose-700'}`}>{i.status.replace('_', ' ')}</span></td>
                <td className="py-2 px-3">
                  <div className="flex gap-2">
                    {i.balance > 0 && (
                      <button data-testid={`pay-stripe-${i.id}`} onClick={() => payMut.mutate(i)} className="text-xs bg-slate-900 text-white rounded px-2 py-1 hover:bg-slate-800 transition-colors">{t('Pay online')}</button>
                    )}
                    {rzpStatus?.configured && i.balance > 0 && (i.currency || 'INR') === 'INR' && (
                      <RazorpayPayButton invoice={i} onPaid={() => qc.invalidateQueries()} />
                    )}
                    {upi?.enabled && i.balance > 0 && (i.currency || 'INR') === 'INR' && (
                      <a data-testid={`pay-upi-${i.id}`} href={upiLink(i)} className="text-xs border border-green-300 text-green-700 rounded px-2 py-1 hover:bg-green-50 transition-colors">Pay via UPI</a>
                    )}
                    {isGuardian && i.balance > 0 && !i.payment_plan && (
                      pendingByInvoice[i.id]
                        ? <span data-testid={`split-pending-${i.id}`} className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-1">Split requested</span>
                        : <button data-testid={`request-split-${i.id}`} onClick={() => requestSplit(i)} className="text-xs border border-slate-200 rounded px-2 py-1 hover:border-orange-500 transition-colors">Request split</button>
                    )}
                    {i.payment_plan && (
                      <span data-testid={`plan-badge-${i.id}`} className="text-xs bg-green-50 text-green-700 border border-green-200 rounded px-2 py-1">Plan: {i.installments} × {money(i.installment_amount, i.currency)}</span>
                    )}
                    {isGuardian && i.payment_plan && i.balance > 0 && (
                      <button data-testid={`autopay-toggle-${i.id}`} onClick={() => autopayMut.mutate({ id: i.id, enabled: !i.autopay?.enabled })}
                        className={`text-xs rounded px-2 py-1 border ${i.autopay?.enabled ? 'bg-orange-50 text-orange-700 border-orange-300' : 'border-slate-200 hover:border-orange-500'}`}>
                        {i.autopay?.enabled ? 'Autopay ON' : 'Enable autopay'}
                      </button>
                    )}
                    {isFin && i.balance > 0 && (
                      <button data-testid={`record-payment-${i.id}`} onClick={() => recordMut.mutate(i)} className="text-xs border border-slate-200 rounded px-2 py-1 hover:border-slate-900 transition-colors">{t('Record installment')}</button>
                    )}
                    {isFin && i.balance > 0 && (
                      <button data-testid={`remind-${i.id}`} onClick={() => remindMut.mutate(i)} className="text-xs border border-amber-300 text-amber-700 rounded px-2 py-1 hover:bg-amber-50 transition-colors">{t('Remind')}</button>
                    )}
                    {isFin && i.balance > 0 && (
                      <button data-testid={`credit-note-${i.id}`} onClick={() => doCredit(i)} className="text-xs border border-blue-200 text-blue-700 rounded px-2 py-1 hover:bg-blue-50 transition-colors">Credit</button>
                    )}
                    {isFin && i.balance > 0 && (
                      <button data-testid={`write-off-${i.id}`} onClick={() => doWriteOff(i)} className="text-xs border border-rose-200 text-rose-600 rounded px-2 py-1 hover:bg-rose-50 transition-colors">Write off</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {(invoices || []).length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">{t('No invoices in your scope.')}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="font-mono-label text-[10px] uppercase text-slate-400 mt-3">Online payments via Stripe (test mode) · Razorpay adapter stubbed</p>

      {statementStudents.length > 0 && (
        <div className="mt-8" data-testid="fee-statements-section">
          <h2 className="font-display text-lg font-semibold mb-1">{t('Fee statements')}</h2>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">Term-wise PDF of every charge, credit and payment</p>
          <div className="flex flex-wrap gap-2">
            {statementStudents.map((s) => (
              <button key={s.id} data-testid={`statement-download-${s.id}`} onClick={() => downloadStatement(s.id, s.name)}
                className="border border-slate-200 bg-white rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-900 hover:text-slate-900 transition-colors">
                {t('Download statement')} - {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {(receipts || []).length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-lg font-semibold mb-1">{t('Receipts')}</h2>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">{t('Paperless - delivered by email & WhatsApp, no printing')}</p>
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="receipts-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr className="text-left">
                {['Receipt #', 'Student', 'Amount', 'Method', 'Balance after', 'Delivery'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}
              </tr></thead>
              <tbody>
                {receipts.map((r, i) => (
                  <tr key={r.id} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/50' : ''}`} data-testid={`receipt-${r.receipt_no}`}>
                    <td className="py-2 px-3 font-mono-label text-xs">{r.receipt_no}</td>
                    <td className="py-2 px-3 font-medium">{r.student_name}</td>
                    <td className="py-2 px-3 text-green-700 font-semibold">₹{r.amount.toLocaleString()}</td>
                    <td className="py-2 px-3">{r.method}</td>
                    <td className="py-2 px-3">₹{r.balance_after.toLocaleString()}</td>
                    <td className="py-2 px-3">
                      <div className="flex gap-1 flex-wrap">
                        <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${r.email_status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-700'}`}>email: {r.email_status === 'sent' ? 'sent' : 'stubbed'}</span>
                        <span className="font-mono-label text-[9px] uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-700">whatsapp: stubbed</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
